import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { PickCard } from '../components/picks/PickCard';
import { MoreSportsStrip } from '../components/picks/MoreSportsStrip';
import { TrackRecord } from '../components/picks/TrackRecord';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FILTERS = ['all', 'actionable', 'monitor'];

const Attribution = ({ testId }) => (
  <span data-testid={testId} className="text-sm text-gray-400 [&_a]:text-[#3B82F6] [&_a]:underline-offset-4 hover:[&_a]:underline">
    <a href="https://sportscore.com/" rel="dofollow">Powered by SportScore</a>
  </span>
);

const usePicksData = () => {
  const [state, setState] = useState({ picks: [], sports: [], loading: true, error: false });
  useEffect(() => {
    let alive = true;
    Promise.all([
      fetch(`${API_URL}/api/sports/picks?limit=50`).then((r) => r.json()),
      fetch(`${API_URL}/api/sports/feeds/summary`).then((r) => r.json()),
    ])
      .then(([p, s]) => alive && setState({ picks: p.picks || [], sports: s.sports || [], loading: false, error: false }))
      .catch(() => alive && setState((prev) => ({ ...prev, loading: false, error: true })));
    return () => { alive = false; };
  }, []);
  return state;
};

const FilterChips = ({ value, onChange, counts }) => (
  <div className="flex gap-2" role="tablist">
    {FILTERS.map((f) => (
      <button
        key={f}
        type="button"
        data-testid={`picks-filter-${f}`}
        onClick={() => onChange(f)}
        className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${value === f ? 'border-[#3B82F6] bg-[#3B82F6]/15 text-white' : 'border-[#1e2a45] text-gray-400 hover:text-white'}`}
      >
        {f} <span className="text-gray-500">{counts[f]}</span>
      </button>
    ))}
  </div>
);

const PicksList = ({ loading, error, picks }) => {
  if (loading) return <div className="flex items-center gap-2 text-gray-400 py-16" data-testid="picks-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading today's edges…</div>;
  if (error) return <p className="py-16 text-gray-400" data-testid="picks-error">We couldn't load today's picks. Please try again shortly.</p>;
  if (!picks.length) return <p className="py-16 text-gray-400" data-testid="picks-empty">No edges right now — new fixtures are checked every few hours.</p>;
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="picks-grid">
      {picks.map((p, i) => <PickCard key={p.id} pick={p} index={i} />)}
    </div>
  );
};

export default function PicksPage() {
  const { picks, sports, loading, error } = usePicksData();
  const [filter, setFilter] = useState('all');
  const counts = useMemo(() => ({
    all: picks.length,
    actionable: picks.filter((p) => p.status === 'actionable').length,
    monitor: picks.filter((p) => p.status === 'monitor').length,
  }), [picks]);
  const visible = filter === 'all' ? picks : picks.filter((p) => p.status === filter);

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="picks-page">
      <SEO
        title="Today's Football Picks & Fair Odds | AMP Predict"
        description="Today's European football edges: margin-free fair odds, home/draw/away probabilities and value flags for the next 72 hours. Trade every fixture with virtual TK¢ on AMP Predict."
        keywords="football picks today, fair odds, value bets, European football predictions, AMP Predict"
        url="https://amppredict.com/picks"
      />
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-20">
        <header className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-[#3B82F6] mb-3">Next 72 hours · European football</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight" data-testid="picks-h1">Today's edges</h1>
          <p className="mt-4 text-base text-gray-300 leading-relaxed">
            Fair odds with the bookmaker margin removed, plus fixtures where a soft bookmaker is pricing above fair value.
            Every fixture is also a live YES/NO market on AMP Predict — trade it with virtual TK¢, no real money.
          </p>
        </header>
        <div className="mt-10 mb-6 flex flex-wrap items-center justify-between gap-4">
          <FilterChips value={filter} onChange={setFilter} counts={counts} />
          <Attribution testId="picks-attribution" />
        </div>
        <PicksList loading={loading} error={error} picks={visible} />
        {sports.length > 0 && <MoreSportsStrip sports={sports} />}
        <TrackRecord />
        <p className="mt-12 text-xs text-gray-500 max-w-3xl">
          Odds are informational and refresh throughout the day. AMP Predict is a play-money simulator for forecasting practice — nothing here is betting advice.
        </p>
      </main>
      <Footer />
    </div>
  );
}
